import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

const PACKAGE_ORDER = ["gold", "silver", "bronze"];

const PACKAGE_ICONS = {
  bronze: "fas fa-award",
  silver: "fas fa-medal",
  gold: "fas fa-trophy",
};

const ASPECT_ICONS = {
  investment: "fas fa-coins",
  logoWebsite: "fas fa-globe",
  eventMaterials: "fas fa-gift",
  printMedia: "fas fa-newspaper",
  socialMedia: "fas fa-hashtag",
  commercials: "fas fa-bullhorn",
  booth: "fas fa-store",
  vipArea: "fas fa-crown",
};

const ASPECT_KEYS = [
  "investment",
  "logoWebsite",
  "eventMaterials",
  "printMedia",
  "socialMedia",
  "commercials",
  "booth",
  "vipArea",
];

const TIER_HEADER = {
  gold: "border-b border-amber-200/80 bg-amber-50/90 text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/35 dark:text-amber-100",
  silver:
    "border-b border-slate-200 bg-slate-100 text-slate-900 dark:border-slate-600 dark:bg-slate-700/45 dark:text-slate-100",
  bronze:
    "border-b border-orange-200/80 bg-orange-50/90 text-orange-950 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-100",
};

const TIER_HEADER_ICON = {
  gold: "text-amber-600 dark:text-amber-400",
  silver: "text-slate-600 dark:text-slate-300",
  bronze: "text-orange-600 dark:text-orange-400",
};

const TIER_CARD = {
  gold: "border-amber-200/90 dark:border-amber-800/60",
  silver: "border-slate-200 dark:border-slate-600",
  bronze: "border-orange-200/90 dark:border-orange-800/60",
};

/** Linker Akzentstreifen nur Mobil-Karten (Gold / Silber / Bronze) */
const TIER_CARD_LEFT = {
  gold: "border-l-amber-500 dark:border-l-amber-400",
  silver: "border-l-slate-400 dark:border-l-slate-300",
  bronze: "border-l-orange-600 dark:border-l-orange-500",
};

const TIER_CARD_HEAD = {
  gold: "bg-amber-50/90 dark:bg-amber-950/35",
  silver: "bg-slate-100 dark:bg-slate-700/45",
  bronze: "bg-orange-50/90 dark:bg-orange-950/30",
};

function SponsoringComparison() {
  const { t } = useTranslation();
  const base = process.env.PUBLIC_URL || "";

  const packages = useMemo(
    () =>
      PACKAGE_ORDER.map((key) => ({
        key,
        label: t(`sponsoringPackagesComparison.packages.${key}`),
      })),
    [t]
  );

  const aspects = useMemo(
    () =>
      ASPECT_KEYS.map((key) => ({
        key,
        label: t(`sponsoringPackagesComparison.aspects.${key}`),
      })),
    [t]
  );

  const getCellText = (pkgKey, aspectKey) => {
    const key = `sponsoringPackagesComparison.cells.${pkgKey}.${aspectKey}`;
    const val = t(key);
    if (!val || val === key) return "";
    return val;
  };

  const packageSubtitle = (pkgKey) => {
    const subKey = `sponsoringPackagesComparison.packages.${pkgKey}Subtitle`;
    const val = t(subKey);
    return val !== subKey ? val : "";
  };

  const eventMapHref = `${base}/documents/Event-Mappe.pdf`;
  const eventMapFileName = "GCWAA-Event-Mappe.pdf";

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-12 transition-colors dark:bg-slate-900 md:pb-16 md:pt-14"
      id="sponsoring-packages"
      aria-labelledby="sponsoring-comparison-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="sponsoring-comparison-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("sponsoringPackagesComparison.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("sponsoringPackagesComparison.description")}
          </p>
        </header>

        {/* Desktop: eine semantische Tabelle */}
        <div className="mb-10 hidden lg:block">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[56rem] border-collapse text-sm">
                <caption className="sr-only">
                  {t("sponsoringPackagesComparison.table_caption")}
                </caption>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="sticky left-0 z-20 w-[min(14rem,22vw)] min-w-[12rem] border-b border-r border-slate-200 bg-white px-4 py-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400"
                    >
                      {t("sponsoringPackagesComparison.aspectsTitle")}
                    </th>
                    {packages.map((p) => {
                      const sub = packageSubtitle(p.key);
                      return (
                        <th
                          key={p.key}
                          scope="col"
                          className={`px-4 py-4 text-center align-bottom ${TIER_HEADER[p.key]}`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <i
                              className={`${PACKAGE_ICONS[p.key]} text-2xl ${TIER_HEADER_ICON[p.key]}`}
                              aria-hidden
                            />
                            <span className="text-base font-semibold">
                              {p.label}
                            </span>
                            {sub ? (
                              <span className="max-w-[12rem] text-xs font-normal leading-snug opacity-90">
                                {sub}
                              </span>
                            ) : null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {aspects.map((a) => (
                    <tr
                      key={a.key}
                      className="group transition-colors hover:bg-slate-50/90 dark:hover:bg-slate-700/25"
                    >
                      <th
                        scope="row"
                        className="sticky left-0 z-10 border-r border-slate-200 bg-white px-4 py-3.5 text-left font-medium text-slate-900 group-hover:bg-slate-50/90 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:group-hover:bg-slate-700/25"
                      >
                        <span className="flex items-center gap-3">
                          <span
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary-light/15"
                            aria-hidden
                          >
                            <i
                              className={`${ASPECT_ICONS[a.key] || "fas fa-circle"} text-sm text-primary dark:text-primary-light`}
                            />
                          </span>
                          <span className="text-pretty leading-snug">
                            {a.label}
                          </span>
                        </span>
                      </th>
                      {packages.map((p) => {
                        const text = getCellText(p.key, a.key);
                        const display =
                          text && text.trim() !== "" ? text : "—";
                        return (
                          <td
                            key={`${p.key}-${a.key}`}
                            className="border-r border-slate-100 px-4 py-3.5 text-center text-slate-700 last:border-r-0 dark:border-slate-700/80 dark:text-slate-300"
                          >
                            <span className="inline-block max-w-xs text-pretty leading-relaxed">
                              {display}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mobil & Tablet: gestapelte Paket-Karten */}
        <div className="mb-10 grid grid-cols-1 gap-5 lg:hidden">
          {packages.map((pkg) => {
            const sub = packageSubtitle(pkg.key);
            return (
              <article
                key={pkg.key}
                className={`overflow-hidden rounded-2xl border border-slate-200 border-l-4 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800 ${TIER_CARD[pkg.key]} ${TIER_CARD_LEFT[pkg.key]}`}
              >
                <div
                  className={`border-b border-slate-200/80 px-5 py-5 dark:border-slate-600 ${TIER_CARD_HEAD[pkg.key]}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/80 shadow-sm dark:bg-slate-800/80"
                      aria-hidden
                    >
                      <i
                        className={`${PACKAGE_ICONS[pkg.key]} text-2xl ${TIER_HEADER_ICON[pkg.key]}`}
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        {pkg.label}
                      </h3>
                      {sub ? (
                        <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                          {sub}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
                <ul className="m-0 list-none space-y-0 divide-y divide-slate-100 p-0 dark:divide-slate-700/80">
                  {aspects.map((aspect) => {
                    const text = getCellText(pkg.key, aspect.key);
                    const display =
                      text && text.trim() !== "" ? text : "—";
                    return (
                      <li key={aspect.key} className="px-5 py-4">
                        <div className="flex gap-3">
                          <span
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary-light/15"
                            aria-hidden
                          >
                            <i
                              className={`${ASPECT_ICONS[aspect.key] || "fas fa-circle"} text-sm text-primary dark:text-primary-light`}
                            />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {aspect.label}
                            </p>
                            <p className="mt-1 text-pretty text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                              {display}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </article>
            );
          })}
        </div>

        <aside
          className="mb-10 overflow-hidden rounded-2xl border border-slate-200 border-l-4 border-l-slate-300 bg-white shadow-sm dark:border-slate-700 dark:border-l-slate-600 dark:bg-slate-800"
          aria-label={t("sponsoringPackagesComparison.title")}
        >
          <div className="flex gap-4 px-5 py-5 sm:gap-5 sm:px-6 sm:py-6">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700/80"
              aria-hidden
            >
              <i className="fas fa-info-circle text-lg text-slate-600 dark:text-slate-300" />
            </div>
            <p className="min-w-0 flex-1 text-pretty text-sm leading-relaxed text-slate-700 dark:text-slate-300 sm:text-base">
              {t("sponsoringPackagesComparison.footnote")}
            </p>
          </div>
        </aside>

        <div className="mx-auto max-w-xl">
          <a
            href={eventMapHref}
            download={eventMapFileName}
            className="group flex items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-sm transition-all dark:border-slate-700 dark:border-l-primary-light dark:bg-slate-800 motion-safe:hover:shadow-md sm:p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/40 dark:focus-visible:ring-offset-slate-900"
            aria-label={t("sponsoringBenefits.download_aria")}
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 dark:bg-primary-light/15 motion-safe:group-hover:scale-105 sm:h-14 sm:w-14"
              aria-hidden
            >
              <i className="fas fa-file-pdf text-xl text-primary dark:text-primary-light sm:text-2xl" />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <span className="block text-base font-semibold text-slate-900 dark:text-slate-100 sm:text-lg">
                {t("sponsoringBenefits.downloadEventmappe")}
              </span>
              <span className="mt-0.5 block text-sm text-slate-500 dark:text-slate-400">
                {t("sponsoringBenefits.download_file_hint")}
              </span>
            </div>
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors group-hover:bg-primary/10 group-hover:text-primary dark:bg-slate-700 dark:text-slate-300 dark:group-hover:bg-primary-light/15 dark:group-hover:text-primary-light"
              aria-hidden
            >
              <i className="fas fa-download text-sm" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default SponsoringComparison;
